import {Component, computed, inject, input, Signal} from '@angular/core';

import {rxResource} from '@angular/core/rxjs-interop';
import {map} from 'rxjs';

import {LoaderComponent, VarianceChartComponent, VarianceChartPoint} from '@selpia/shared/ui';
import {ArrayTools, DataBatchTools, Formatter} from '@selpia/shared/utils';
import {Alignment, Measure, Perspective, Sentiment} from '@selpia/shared/models';
import {DataBatchService, DTOsConverter} from '@selpia/data-batch/data-access';

@Component({
  selector: 'lib-breakdown-chart',
  imports: [VarianceChartComponent, LoaderComponent],
  templateUrl: './breakdown-chart.component.html',
  styleUrl: './breakdown-chart.component.scss',
})
export class BreakdownChartComponent {
  readonly #dataBatchService = inject(DataBatchService);

  readonly perspective = input.required<Perspective>();
  readonly measure = input.required<Measure | undefined>();
  readonly growthMeasure = input.required<Measure | undefined>();
  readonly period = input.required<string | undefined>();

  protected mainFormatter = computed(() => {
    const measure = this.measure();
    return (value: number): string => Formatter.formatByMeasure(value, measure, true);
  });

  readonly #dataBatchResource = rxResource({
    request: () => this.perspective(),
    loader: ({request: perspective}) => {
      const {focusDimensionLevel, filterDimensions, includeTotal, date} = perspective;
      return this.#dataBatchService.loadDataBatch(
        focusDimensionLevel.name,
        DTOsConverter.buildFilters(filterDimensions, includeTotal, date),
      )
        .pipe(map(dataBatch => DTOsConverter.dtoDataBatch(dataBatch.dataChunks)));
    },
  });

  protected readonly isLoading = this.#dataBatchResource.isLoading;

  protected readonly chartData: Signal<VarianceChartPoint[]> = computed(() => {
    const dataBatch = this.#dataBatchResource.value();
    const measureCode = this.measure()?.code;
    const periodCode = this.period();
    const growthMeasure = this.growthMeasure();
    if (!dataBatch || !measureCode || !periodCode) {
      return [];
    }

    const filterParams = {measureCode, periodCode, date: this.perspective().date};

    const chartDataPoints: Array<VarianceChartPoint | undefined> = dataBatch.map(dataChunk => {
      const label = DataBatchTools.findDimensionItemBy(
        {dimensionLevelName: this.perspective().focusDimensionLevel.name},
        dataChunk.dimensionItems,
      )?.name;
      if (!label) {
        return undefined;
      }

      const current = DataBatchTools.getDataPointFor(dataChunk.dataPoints, filterParams)?.value;
      if (!current) {
        return undefined;
      }

      const previous = DataBatchTools.getDataPointFor(dataChunk.dataPoints, {
        ...filterParams,
        measureCode: `${measureCode}_PY`,
      })?.value;
      if (!previous) {
        return undefined;
      }

      const variance = DataBatchTools.getDataPointFor(dataChunk.dataPoints, {
        ...filterParams,
        measureCode: growthMeasure?.code,
      })?.value;

      const varianceLabel = Formatter.formatByMeasure(variance, growthMeasure);

      return {
        current,
        previous,
        label,
        varianceLabel,
      };
    });

    return ArrayTools.removeFalsy(chartDataPoints)
      .sort((a, b) => b.current - a.current);
  });


  protected readonly Sentiment = Sentiment;
  protected readonly Alignment = Alignment;
}
